// Integration Icons
// -------------------
// SVG icons for each integration type. Clean, monochrome, recognizable.

import React from "react";

const ICON_SIZE = 32;

interface IconProps {
  size?: number;
  className?: string;
}

// Jira — simplified ticket icon
export const JiraIcon: React.FC<IconProps> = ({ size = ICON_SIZE, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V2.84a.84.84 0 0 0-.84-.84H11.53zM6.77 6.8a4.36 4.36 0 0 0 4.34 4.34h1.78v1.72a4.36 4.36 0 0 0 4.34 4.34V7.63a.84.84 0 0 0-.83-.83H6.77zM2 11.6a4.35 4.35 0 0 0 4.34 4.34h1.78v1.72c0 2.4 1.95 4.34 4.35 4.34v-9.57a.84.84 0 0 0-.84-.83H2z" />
  </svg>
);

// Slack — simplified hash/pound icon
export const SlackIcon: React.FC<IconProps> = ({ size = ICON_SIZE, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.27 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.163 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.163 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.163 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.27a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.315A2.528 2.528 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.523h-6.315z" />
  </svg>
);

// Teams — simplified people icon
export const TeamsIcon: React.FC<IconProps> = ({ size = ICON_SIZE, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.625 8.5h-3.75c.759 0 1.375-.616 1.375-1.375S17.634 5.75 16.875 5.75a1.375 1.375 0 0 0 0 2.75h-1.5v4.125c0 1.036.839 1.875 1.875 1.875h3.375c1.036 0 1.875-.839 1.875-1.875V10.5c0-1.105-.895-2-2-2zM9.5 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm5.25 2.5h-.434a6.907 6.907 0 0 1-4.816 0H9.25A4.25 4.25 0 0 0 5 14.75v1.125C5 16.496 5.504 17 6.125 17h6.75c.621 0 1.125-.504 1.125-1.125V14.75a4.25 4.25 0 0 0-4.25-4.25h4.75z" />
  </svg>
);

// Webhook — code brackets icon
export const WebhookIcon: React.FC<IconProps> = ({ size = ICON_SIZE, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
    <line x1="12" y1="2" x2="12" y2="22" />
  </svg>
);

// Splunk — simplified chart/search icon
export const SplunkIcon: React.FC<IconProps> = ({ size = ICON_SIZE, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M3 3v18h18V3H3zm16 16H5V5h14v14zM7 13h2v4H7v-4zm4-4h2v8h-2V9zm4-2h2v10h-2V7z" />
  </svg>
);

// Sentinel — shield icon
export const SentinelIcon: React.FC<IconProps> = ({ size = ICON_SIZE, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
  </svg>
);

// Enabled integration types.
// ---------------------------
// Only Slack, Jira, and Teams are offered today. Webhook / Splunk / Sentinel
// adapters exist on the backend but are intentionally withheld until each has
// been verified end-to-end. This is mirrored (and enforced) server-side by
// ENABLED_INTEGRATION_TYPES in models/definitions.py — the two must stay in
// sync. The unused icon components above are kept for when those types return.
//
// Map integration type to icon component
export const INTEGRATION_ICON_MAP: Record<string, React.FC<IconProps>> = {
  jira: JiraIcon,
  slack: SlackIcon,
  teams: TeamsIcon,
};

// Integration type metadata
export const INTEGRATION_TYPE_META: Record<string, { displayName: string; description: string }> = {
  jira: { displayName: "Jira", description: "Issue tracking" },
  slack: { displayName: "Slack", description: "Notifications" },
  teams: { displayName: "Teams", description: "Notifications" },
};
